import { CurrencyCodes, SecurityTypes } from '@onevesthq/ov-enums';
import { TranslatedString } from './shared';

export enum FinancialProductState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum Strategy {
  STANDARD = 'STANDARD',
}

export enum PortfolioTemplateState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum Exchanges {
  TSX = 'TSX',
  NASDAQ = 'NASDAQ',
  NYSE = 'NYSE',
  FUNDSERV = 'FUNDSERV',
  PTF = 'PTF',
  PRIVATE_SECURITY = 'PRIVATE_SECURITY',
  CANNEX = 'CANNEX',
  CBOE = 'CBOE',
  CBOE_CAD = 'CBOE_CAD',
  CME = 'CME',
  CHX = 'CHX',
  ISE = 'ISE',
  PHLX = 'PHLX',
  MS4X = 'MS4X',
  TSX_V = 'TSX_V',
}

export const EXCHANGE_COUNTRIES = {
  US: 'US',
  CA: 'CA',
};

export const EXCHANGES_COUNTRIES_MAP = {
  [Exchanges.NASDAQ.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.NYSE.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CBOE.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CBOE_CAD.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.CME.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CHX.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.ISE.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.PHLX.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.MS4X.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.TSX.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.TSX_V.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.FUNDSERV.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.CANNEX.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.PTF.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.PRIVATE_SECURITY.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
};

export enum ThemeState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface Theme {
  id?: string
  name?: string
  description?: string
  translatedName?: TranslatedString
  translatedDescription?: TranslatedString
  translatedShortDescription?: TranslatedString
  key?: string
  core?: boolean
  state?: ThemeState
  iconKey?: string
}

export interface AssetClass {
  id?: string | null
  key?: string
  name?: string
  description?: string
  translatedName?: TranslatedString
  translatedDescription?: TranslatedString
  state?: string;
  riskLevel?: number,
}

export interface Branch {
  id?: string
  percentage: number
  financialProduct: FinancialProduct
  taxRank?: number
  isCashEquivalent?: boolean
}

export interface AssetClassBranch {
  id: string;
  targetPercentage: number | null;
  assetClass: AssetClass;
  color: 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix';
}

export interface PortfolioTemplate {
  id: string;
  name: string;
  state: PortfolioTemplateState;
  translatedName: TranslatedString | undefined;
  translatedTemplateDescription: TranslatedString | undefined;
  assetClassBranches: AssetClassBranch[] | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface TargetAllocation {
  secondaryAssetClass: AssetClass
  min: number
  max: number
}

export interface Schedule {
  dates?: string[];
  nextDate?: string;
  id?: string
  name?: string
  associatedFinancialProductsCount?: number,
}

export interface FinancialProduct {
  id?: string
  name?: string
  url?: string
  translatedName?: TranslatedString
  translatedPortfolioDisclaimer?: TranslatedString
  state?: FinancialProductState
  suggestedMinScore?: number
  suggestedMaxScore?: number
  lowThresholdScore?: number
  tradeable?: boolean
  suggestable?: boolean
  autoUpdatePrices?: boolean
  assignable?: boolean
  cusip?: string | null
  ticker?: string | null
  exchange?: Exchanges
  ric?: string
  forecastedRateOfReturn?: number
  forecastedStandardDeviation?: number
  currentPriceCents?: number
  children?: Branch[]
  theme?: Theme
  primaryAssetClass?: AssetClass
  secondaryAssetClass?: AssetClass
  tertiaryAssetClass?: AssetClass
  portfolioDescription?: string
  translatedPortfolioDescription?: TranslatedString
  investmentObjective?: string
  translatedInvestmentObjective?: TranslatedString
  translatedLiquidity?: TranslatedString
  targetAllocations?: TargetAllocation[]
  updatedAt?: Date
  riskReturnLevel?: number
  reasonForUpdate?: string
  translatedReasonForUpdate?: TranslatedString
  isCash?: boolean
  substituteFinancialProduct?: FinancialProduct
  isPartial?: boolean
  taxRank?: number | null
  settlementDays?: number | null
  currency?: CurrencyCodes
  originalCurrency?: CurrencyCodes
  translatedNickName?: TranslatedString
  riskProfileName?: TranslatedString
  portfolioTemplate?: PortfolioTemplate
  zeroPercentCash?: boolean
  strategy?: Strategy
  type?: SecurityTypes,
  schedule?: Schedule
  isin?: string
  volatile?: boolean
  waivedFeePercentage?: number | null;
  multiplier?: number | null
}
